import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
  Button,
  Input,
  FormGroup,
  Label,
  Spinner,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import "react-notification-alert/dist/animate.css";
import { useNavigate } from "react-router-dom";
import { CursoService } from "service/CursoService";
import CustomPagination from "components/Pagination/Pagination";
import ModalCurso from "components/Modals/ModalCurso";
import FSelect from "components/Select/FSelect";

function Cursos() {
  const notificationAlertRef = useRef(null);

  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tc",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const [listCursos, setListCursos] = useState([]);
  const [listFiltered, setListFiltered] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterEstado, setFilterEstado] = useState("");

  const [loadingBuscar, setLoadingBuscar] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCurso, setSelectedCurso] = useState(null);

  const cursoService = new CursoService();
  const navigate = useNavigate();

  // Sin auto-load: el usuario activa la búsqueda con el botón "Buscar"

  const applyFilters = (data, query, estado) => {
    let filtered = data;
    if (query) {
      filtered = filtered.filter(item =>
        (item.titulo || "").toLowerCase().includes(query) ||
        (item.modulo?.titulo || "").toLowerCase().includes(query) ||
        (item.modulo?.denominacion || "").toLowerCase().includes(query)
      );
    }
    if (estado) {
      filtered = filtered.filter(item => item.estado === estado);
    }
    return filtered;
  };

  const getCursos = async () => {
    setLoadingBuscar(true);
    try {
      const response = await cursoService.getCursos();
      const data = response?.data || [];
      setListCursos(data);
      setListFiltered(applyFilters(data, searchQuery, filterEstado));
      if (data.length === 0) notify("warning", "No se encontraron cursos registrados.");
      else notify("success", `${data.length} curso(s) encontrado(s).`);
    } catch (error) {
      console.error("Error cargando cursos:", error);
      notify("danger", "Error al obtener los cursos. Intenta nuevamente.");
    } finally {
      setLoadingBuscar(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setListFiltered(applyFilters(listCursos, query, filterEstado));
    setCurrentPage(1);
  };

  const handleEstadoFilter = (e) => {
    const estado = e.target.value;
    setFilterEstado(estado);
    setListFiltered(applyFilters(listCursos, searchQuery, estado));
    setCurrentPage(1);
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) setSelectedCurso(null);
  };

  const handleEdit = (curso) => {
    setSelectedCurso(curso);
    setModalOpen(true);
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = listFiltered.slice(indexOfFirstItem, indexOfLastItem);

  const goToDetalle = (id) => {
    navigate(`/admin/curso-detalle/${id}`);
  };

  return (
    <>
      <NotificationAlert ref={notificationAlertRef} />
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              {/* Zona 1: Header */}
              <CardHeader>
                <Row className="align-items-center">
                  <Col md="8">
                    <CardTitle tag="h4">Administración de Cursos</CardTitle>
                  </Col>
                  <Col md="4" className="text-right">
                    <Button
                      color="link"
                      className="btn-icon btn-round"
                      title="Configuración"
                    >
                      <i className="tim-icons icon-settings-gear-63" />
                    </Button>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                {/* Zona 2: Barra de filtros */}
                <Row className="align-items-end mb-4">
                  <Col md="6">
                    <FormGroup className="mb-0">
                      <Label for="search">Filtrar por Título o Módulo</Label>
                      <Input
                        type="text"
                        id="search"
                        placeholder="Ingrese términos de búsqueda (título, módulo...)"
                        value={searchQuery}
                        onChange={handleSearch}
                        style={{ height: "40px" }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="5">
                    <FormGroup className="mb-0">
                      <Label>Estado</Label>
                      <FSelect
                        value={filterEstado}
                        onChange={handleEstadoFilter}
                        options={[
                          { value: "", label: "Todos" },
                          { value: "Publicado", label: "Publicado" },
                          { value: "Borrador", label: "Borrador" },
                          { value: "Oculto", label: "Oculto" },
                        ]}
                        placeholder="Todos los estados"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="1">
                    <FormGroup className="mb-0">
                      <Button
                        color="info"
                        block
                        onClick={getCursos}
                        disabled={loadingBuscar}
                        style={{ height: "40px", padding: "0" }}
                        title="Buscar"
                      >
                        {loadingBuscar ? (
                          <Spinner size="sm" />
                        ) : (
                          <i className="tim-icons icon-zoom-split" />
                        )}
                      </Button>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Zona 3: Barra de acciones */}
                <Row className="mb-4">
                  <Col md="6">
                    {/* Espacio reservado para acciones masivas */}
                  </Col>
                  <Col md="6" className="text-right">
                    <Button
                      color="primary"
                      onClick={toggleModal}
                      className="btn-fill"
                    >
                      <i className="tim-icons icon-simple-add mr-2" />
                      Agregar Curso
                    </Button>
                  </Col>
                </Row>

                <hr className="my-4" />

                <Table>
                  <thead className="text-primary">
                    <tr>
                      <th>Título</th>
                      <th>Módulo</th>
                      <th>Estado</th>
                      <th className="text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((row, index) => (
                      <tr key={row._id || index}>
                        <td>{row.titulo}</td>
                        <td>
                          {row.modulo ? `${row.modulo.titulo} ${row.modulo.denominacion}` : "-"}
                        </td>
                        <td>{row.estado}</td>
                        <td className="text-right">
                          <Button 
                            color="info" 
                            size="sm" 
                            className="btn-icon btn-link" 
                            title="Gestionar Temas y Contenido"
                            onClick={() => goToDetalle(row._id)}
                          >
                            <i className="tim-icons icon-settings-gear-63" />
                          </Button>
                          <Button 
                            color="warning" 
                            size="sm" 
                            className="btn-icon btn-link" 
                            title="Editar Datos Básicos"
                            onClick={() => handleEdit(row)}
                          >
                            <i className="tim-icons icon-pencil" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {listFiltered.length === 0 && (
                      <tr>
                        <td colSpan="4" className="text-center">No se encontraron cursos</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                <CustomPagination
                  itemsPerPage={itemsPerPage}
                  totalItems={listFiltered.length}
                  paginate={paginate}
                  currentPage={currentPage}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <ModalCurso 
        isOpen={modalOpen} 
        toggle={toggleModal} 
        selectedCurso={selectedCurso} 
        onSave={getCursos} 
      />
    </>
  );
}

export default Cursos;
